import React, { useMemo } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import { useBetaData } from '../../../contexts/BetaDataContext';
import useTicketSearch from '../../../hooks/useTicketSearch';
import TicketFilterBar from '../filters/TicketFilterBar';
import Pagination from '../common/Pagination';
import TicketTable from '../common/TicketTable';
import './GlobalSearchView.scss';

const GlobalSearchView = ({ onNavigate, refreshKey = 0 }) => {
  const { profile, viewingCompanyId } = useAuth();
  const { statusOptions, companyUsers, usersMapByEmail } = useBetaData();

  const companyId = viewingCompanyId === undefined ? undefined : (viewingCompanyId || profile?.company_id);

  const {
    tickets,
    totalCount,
    loading,
    search,
    handleSearchChange,
    statusFilter,
    setStatusFilter,
    priorityFilter,
    setPriorityFilter,
    assigneeFilter,
    setAssigneeFilter,
    page,
    setPage,
    totalPages,
    from,
    to,
    sortBy,
    sortOrder,
    handleSort,
    activeFilterCount,
    clearAllFilters,
  } = useTicketSearch({
    companyId,
    viewerUserId: profile?.id,
    defaultIncludeClosed: true,
    extendedSearch: true,
    statusOptions: statusOptions || [],
    refreshKey,
  });

  const userMap = useMemo(() => {
    const map = {};
    (companyUsers || []).forEach((u) => {
      map[u.id] = u;
    });
    return map;
  }, [companyUsers]);

  const handleRowClick = (ticket) => {
    onNavigate('ticket', ticket.project_id, ticket.id, { ticketNo: ticket.ticket_no });
  };

  const emptyState = (
    <div className="list-empty-state">
      {activeFilterCount > 0 || search ? (
        <>
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#ccc" strokeWidth="1.5">
              <circle cx="20" cy="20" r="14" />
              <path d="M30 30l10 10" strokeLinecap="round" />
            </svg>
          </div>
          <p className="empty-title">沒有符合條件的 Ticket</p>
          <p className="empty-desc">試試調整搜尋條件或篩選設定</p>
          <button className="empty-clear-btn" onClick={clearAllFilters}>
            清除所有篩選
          </button>
        </>
      ) : (
        <>
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 48 48" fill="none" stroke="#ccc" strokeWidth="1.5">
              <rect x="8" y="8" width="32" height="32" rx="4" />
              <path d="M16 24l6 6 10-12" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </div>
          <p className="empty-title">尚無 Ticket</p>
          <p className="empty-desc">目前沒有任何 Ticket 資料</p>
        </>
      )}
    </div>
  );

  return (
    <div className="global-search-view">
      <div className="global-search-header">
        <h2 className="global-search-title">全系統 Ticket 查詢</h2>
        <span className="global-search-count">共 {totalCount} 筆</span>
      </div>

      <TicketFilterBar
        search={search}
        onSearchChange={handleSearchChange}
        statusFilter={statusFilter}
        onStatusFilterChange={setStatusFilter}
        priorityFilter={priorityFilter}
        onPriorityFilterChange={setPriorityFilter}
        assigneeFilter={assigneeFilter}
        onAssigneeFilterChange={setAssigneeFilter}
        activeFilterCount={activeFilterCount}
        onClearAll={clearAllFilters}
        statusOptions={statusOptions || []}
        companyUsers={companyUsers}
        searchPlaceholder="搜尋標題、描述、留言、ToDo... (#編號 搜尋指定任務)"
      />

      <TicketTable
        tickets={tickets}
        loading={loading}
        sortBy={sortBy}
        sortOrder={sortOrder}
        handleSort={handleSort}
        onRowClick={handleRowClick}
        statusOptions={statusOptions}
        userMap={userMap}
        usersMapByEmail={usersMapByEmail}
        showProject
        emptyState={emptyState}
      />

      {!loading && tickets.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={totalCount}
          from={from}
          to={to}
          onPageChange={setPage}
        />
      )}
    </div>
  );
};

export default GlobalSearchView;
